import React, { FC, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import i18n from 'i18next';
import { KeyRound, Loader2, Sparkles, Globe, Languages } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RetroGrid } from '../../components/ui/retro-grid';
import { ShimmerButton } from '../../components/ui/shimmer-button';
import LanguageFlag from '../../shared/components/LanguageFlag';
import { APP_LANGUAGE_OPTIONS, getLanguageOption } from '../../shared/utils/flag-utils';

import { useLogin } from './login.actions';
import { FormInputs } from './login.types';

import styles from './login.module.scss';

const LoginPage: FC = () => {
  const { t } = useTranslation(); // <-- Хук для получения переводов
  const [response, handleLoginSubmit, loading, loginError] = useLogin();

  // Очищаем флаг принудительного выхода при загрузке страницы логина
  useEffect(() => {
    console.log('🔍 Login page mounted');
    localStorage.removeItem('forceLogout');
    localStorage.removeItem('authData');
  }, []);

  // Обработка успешного входа
  useEffect(() => {
    if (response) {
      console.log('🔍 Login successful, redirecting to dashboard...');
      window.location.href = '/';
    }
  }, [response]);

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue, // Для программного задания значения ключа
  } = useForm<FormInputs>();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Открытие меню выбора языка
  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Закрытие меню выбора языка
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Смена языка
  const handleLanguageChange = (lng: string) => {
    i18n.changeLanguage(lng); // Сменить язык
    setAnchorEl(null); // Закрыть меню
  };

  // Автоматически обрабатывать ключ из URL и отправлять форму
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get('key');

    if (key) {
      setValue('key', key); // Устанавливаем значение ключа в форму
      handleSubmit(onSubmit)(); // Автоматически отправляем форму
    }
  }, [setValue, handleSubmit]);

  const onSubmit = (data: FormInputs) => {
    handleLoginSubmit(data);
  };

  return (
    <div className={styles.loginPage}>
      {/* Фоновая ретро-сетка */}
      <RetroGrid
        className="absolute inset-0"
        angle={65}
        cellSize={80}
        opacity={0.25}
        lightLineColor="#06b6d4"
      />

      {/* Градиентный оверлей */}
      <div className={styles.backgroundGradient} />

      {/* Селектор языка */}
      <div className={styles.languageSelector}>
        <span className="text-sm text-white/90 mr-2 flex items-center gap-1.5">
          <Languages className="w-4 h-4" />
          {t('global.language')}
        </span>
        <button
          onClick={handleOpenMenu}
          className="flex items-center justify-center min-w-[68px] h-10 px-3 rounded-lg bg-black/40 backdrop-blur-md text-white border border-white/10 hover:bg-black/60 hover:scale-105 transition-all duration-300 text-sm font-semibold"
        >
          {getLanguageOption(i18n.language).shortLabel}
        </button>

        {/* Простое выпадающее меню */}
        {anchorEl && (
          <div
            className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl overflow-hidden z-50"
            style={{ minWidth: '200px' }}
          >
            {APP_LANGUAGE_OPTIONS.map((languageOption) => (
              <button
                key={languageOption.code}
                onClick={() => handleLanguageChange(languageOption.code)}
                className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors flex items-center gap-3"
              >
                <LanguageFlag language={languageOption.code} size={14} />
                <span>{t(`global.languages.${languageOption.translationKey}`)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Основной контент */}
      <div className="flex flex-col items-center justify-center min-h-screen px-4 relative z-10">
        <Card className="w-full max-w-md backdrop-blur-xl bg-white/95 shadow-2xl border-white/20 animate-fade-in">
          <CardHeader className="space-y-4 text-center pb-4">
            {/* Логотип */}
            <div className="flex justify-center mb-2">
              <img
                src="/Logo.png"
                alt="GameInvitation"
                className="h-24 w-auto object-contain"
              />
            </div>

            {/* Заголовок */}
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              {(() => {
                const welcomeText = t('login.welcome') || 'Создайте игру в подарок за 5 минут';
                const parts = welcomeText.split(' в подарок');
                return parts.length > 1 ? (
                  <>
                    {parts[0]}
                    <br />
                    в подарок{parts[1]}
                  </>
                ) : (
                  welcomeText
                );
              })()}
            </CardTitle>

            {/* Описания */}
            <CardDescription className="text-base">
              {t('login.subtitle') || 'Превратите ваши эмоции в уникальную цифровую открытку-игру'}
            </CardDescription>

            <p className="text-sm text-muted-foreground opacity-80">
              {t('login.description') || 'Загрузите фото, добавьте музыку и порадуйте близких оригинальным подарком'}
            </p>
          </CardHeader>

          <CardContent className="space-y-4">
            <form onSubmit={(...args) => void handleSubmit(onSubmit)(...args)} className="space-y-4">
              {/* Поле ввода ключа */}
              <div className="space-y-2">
                <Label htmlFor="key" className="text-sm font-medium">
                  {t('login.enterKey') || 'Ключ активации'}
                </Label>
                <Controller
                  name="key"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-500">
                        <KeyRound className="w-5 h-5" />
                      </div>
                      <Input
                        {...field}
                        id="key"
                        placeholder={t('login.enterKeyPlaceholder') || 'Введите ключ для доступа в конструктор'}
                        className={`pl-10 h-12 text-base bg-white text-slate-700 placeholder:text-gray-1000 focus:border-cyan-500 focus:ring-cyan-500 ${errors?.key ? 'border-red-500' : 'border-gray-300'}`}
                      />
                    </div>
                  )}
                />
                {errors?.key && (
                  <p className="text-sm text-red-500 mt-1">
                    {t('login.keyRequired') || 'Введите ключ активации, чтобы начать создавать игру'}
                  </p>
                )}
                {loginError.message && (
                  <p className="text-sm text-red-500 mt-1">
                    {loginError.message}
                  </p>
                )}
              </div>

              {/* Кнопка входа */}
              <ShimmerButton
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-semibold"
                shimmerColor="#ffffff"
                background="linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #8b5cf6 100%)"
                borderRadius="0.5rem"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('login.loading') || 'Вход в конструктор...'}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    {t('login.login') || 'Начать создавать'}
                  </div>
                )}
              </ShimmerButton>
            </form>
          </CardContent>

          <CardFooter className="flex flex-col items-center space-y-2 pb-6">
            <p className="text-sm text-muted-foreground text-center">
              {t('login.footerText') || 'Нет ключа? Получите его на нашем сайте'}
            </p>
            <a
              href="https://gameinvitation.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-cyan-600 hover:text-cyan-700 font-semibold text-sm transition-all hover:-translate-y-0.5"
            >
              <Globe className="w-4 h-4" />
              gameinvitation.com
            </a>
          </CardFooter>
        </Card>
      </div>

      {/* Клик вне меню для закрытия */}
      {anchorEl && (
        <div
          className="fixed inset-0 z-40"
          onClick={handleCloseMenu}
        />
      )}
    </div>
  );
};

export default LoginPage;
